// Feed SSE events (FeedSseEvent) → targeted cache invalidation. Pure planning is separate from
// applying so the mapping can be unit-tested: a card event refreshes that card and its board,
// a work event refreshes the work lists and the card carrying that work, a notification refreshes
// the bell (and task lists for reminders), a brief refreshes the brief list that drives the
// banner, a budget reading refreshes the spend page, a task or reply event refreshes the task
// lists or the reply badges, a sender event refreshes the screener and the unsubscribe ladder,
// and so on. A work event also refreshes compose drafts, ladder views and the thread actions
// started from the inbox (and the thread lists they change): a send, a one-click unsubscribe
// or an archive from the list is card-less executor work. Nothing here refetches the whole app
// state.
import type { Query, QueryClient } from "@tanstack/react-query";
import type { BoardView, FeedSseEvent } from "@shared/api";
import { feedKeys } from "./feedQueries";

export const FEED_EVENT_TYPES = [
  "card",
  "work",
  "pass",
  "routine",
  "sweep",
  "proposal",
  "budget",
  "notification",
  "brief",
  "task",
  "reply",
  "sender",
  "calendar",
] as const satisfies readonly FeedSseEvent["type"][];

export interface FeedInvalidationPlan {
  /** Feed ids whose board must refetch. */
  boards: string[];
  /** Card ids whose full view must refetch. */
  cards: string[];
  /** Feed ids whose work lists must refetch. */
  workFeeds: string[];
  /** Work ids that changed; the cards carrying them (found in the board cache) refetch too. */
  workIds: string[];
  /** Routine group ids that changed. */
  routineGroups: string[];
  /** Card ids whose calendar holds changed. */
  calendarCards: string[];
  proposals: boolean;
  /** Documents may have changed (an applied proposal writes one). */
  docs: boolean;
  /** Feed summaries (pass number) changed. */
  feeds: boolean;
  /** Latest budget reading, patched into cached boards without a refetch. */
  budget: { spentUsd: number; throttled: boolean } | null;
  /** The spend report (/settings/spend) is out of date. */
  usage: boolean;
  /** The bell and the notifications drawer. */
  notifications: boolean;
  /** The brief list (banner, /brief). */
  briefs: boolean;
  /** Task lists (a reminder fired, or a task changed on the server). */
  tasks: boolean;
  /** Reply tracking ("Waiting for a reply", the card badges). */
  replies: boolean;
  /** Sender stats, the screener lists and the unsubscribe ladder views. */
  senders: boolean;
  /** Compose drafts (their send is executor work with no card). */
  compose: boolean;
  /** Thread actions from the inbox views (work by id, receipts) and the thread lists. */
  threadActions: boolean;
  /** "Your day": calendar writes (focus blocks, holds, bookings) and tasks change it. */
  agenda: boolean;
}

export function emptyFeedPlan(): FeedInvalidationPlan {
  return {
    boards: [],
    cards: [],
    workFeeds: [],
    workIds: [],
    routineGroups: [],
    calendarCards: [],
    proposals: false,
    docs: false,
    feeds: false,
    budget: null,
    usage: false,
    notifications: false,
    briefs: false,
    tasks: false,
    replies: false,
    senders: false,
    compose: false,
    threadActions: false,
    agenda: false,
  };
}

export function isFeedEvent(event: { type: string }): event is FeedSseEvent {
  return (FEED_EVENT_TYPES as readonly string[]).includes(event.type);
}

export function planFeedInvalidation(events: readonly FeedSseEvent[]): FeedInvalidationPlan {
  const boards = new Set<string>();
  const cards = new Set<string>();
  const workFeeds = new Set<string>();
  const workIds = new Set<string>();
  const routineGroups = new Set<string>();
  const calendarCards = new Set<string>();
  const plan = emptyFeedPlan();
  for (const event of events) {
    switch (event.type) {
      case "card":
        boards.add(event.feedId);
        cards.add(event.cardId);
        break;
      case "work":
        boards.add(event.feedId);
        workFeeds.add(event.feedId);
        workIds.add(event.workId);
        // Card-less executor work: a compose send, a one-click unsubscribe POST or a thread
        // action from the inbox views.
        plan.compose = true;
        plan.senders = true;
        plan.threadActions = true;
        // A focus block is card-less executor work too.
        plan.agenda = true;
        break;
      case "pass":
        boards.add(event.feedId);
        plan.feeds = true;
        break;
      case "routine":
        boards.add(event.feedId);
        routineGroups.add(event.groupId);
        break;
      case "sweep":
        boards.add(event.feedId);
        break;
      case "proposal":
        plan.proposals = true;
        if (event.status === "applied") plan.docs = true;
        break;
      case "budget":
        plan.budget = { spentUsd: event.spentUsd, throttled: event.throttled };
        plan.usage = true;
        break;
      case "notification":
        plan.notifications = true;
        if (event.kind === "reminder") plan.tasks = true;
        if (event.kind === "brief") plan.briefs = true;
        break;
      case "brief":
        plan.briefs = true;
        plan.notifications = true;
        break;
      case "task":
        plan.tasks = true;
        plan.agenda = true;
        break;
      case "reply":
        plan.replies = true;
        break;
      case "calendar":
        if (event.cardId) calendarCards.add(event.cardId);
        plan.agenda = true;
        break;
      case "sender":
        plan.senders = true;
        break;
    }
  }
  plan.boards = [...boards];
  plan.cards = [...cards];
  plan.workFeeds = [...workFeeds];
  plan.workIds = [...workIds];
  plan.routineGroups = [...routineGroups];
  plan.calendarCards = [...calendarCards];
  return plan;
}

/** Card ids in cached boards whose current work item is one of `workIds`. */
export function cardsCarryingWork(
  boards: readonly (BoardView | undefined)[],
  workIds: ReadonlySet<string>,
): string[] {
  const out = new Set<string>();
  for (const board of boards) {
    for (const card of board?.cards ?? []) {
      if (card.work && workIds.has(card.work.id)) out.add(card.id);
    }
  }
  return [...out];
}

export function applyFeedPlan(queryClient: QueryClient, plan: FeedInvalidationPlan): void {
  for (const feedId of plan.boards) {
    void queryClient.invalidateQueries({ queryKey: feedKeys.board(feedId) });
    void queryClient.invalidateQueries({ queryKey: feedKeys.snoozed(feedId) });
  }
  const cardIds = new Set(plan.cards);
  if (plan.workIds.length) {
    const boards = queryClient.getQueriesData<BoardView>({ queryKey: ["board"] }).map(([, b]) => b);
    for (const id of cardsCarryingWork(boards, new Set(plan.workIds))) cardIds.add(id);
  }
  for (const cardId of cardIds) {
    void queryClient.invalidateQueries({ queryKey: feedKeys.card(cardId), exact: true });
  }
  for (const feedId of plan.workFeeds) {
    void queryClient.invalidateQueries({ queryKey: ["work", feedId] });
  }
  if (plan.proposals) void queryClient.invalidateQueries({ queryKey: ["proposals"] });
  if (plan.docs) {
    void queryClient.invalidateQueries({ queryKey: feedKeys.docs });
    void queryClient.invalidateQueries({ queryKey: ["doc"] });
    void queryClient.invalidateQueries({ queryKey: ["feedConfig"] });
  }
  if (plan.feeds) void queryClient.invalidateQueries({ queryKey: feedKeys.feeds });
  if (plan.notifications) void queryClient.invalidateQueries({ queryKey: ["notifications"] });
  if (plan.briefs) {
    void queryClient.invalidateQueries({ queryKey: ["briefs"] });
    // An open brief page reads one brief; its summary arrives after the brief itself.
    void queryClient.invalidateQueries({ queryKey: ["brief"] });
  }
  if (plan.tasks) {
    void queryClient.invalidateQueries({ queryKey: ["tasks"] });
    void queryClient.invalidateQueries({ queryKey: ["task"] });
  }
  if (plan.replies) void queryClient.invalidateQueries({ queryKey: ["replies"] });
  for (const cardId of plan.calendarCards) {
    void queryClient.invalidateQueries({ queryKey: ["calendarHolds", cardId] });
  }
  if (plan.senders) {
    void queryClient.invalidateQueries({ queryKey: ["senders"] });
    void queryClient.invalidateQueries({ queryKey: ["sender"] });
    void queryClient.invalidateQueries({ queryKey: ["unsubscribe"] });
  }
  if (plan.compose) {
    void queryClient.invalidateQueries({ queryKey: ["compose"] });
    void queryClient.invalidateQueries({ queryKey: ["composeDraft"] });
  }
  if (plan.threadActions) {
    void queryClient.invalidateQueries({ queryKey: ["threadWork"] });
    void queryClient.invalidateQueries({ queryKey: ["threads"] });
  }
  if (plan.usage) void queryClient.invalidateQueries({ queryKey: ["llmUsage"] });
  if (plan.agenda) void queryClient.invalidateQueries({ queryKey: ["agenda"] });
  if (plan.budget) {
    const reading = plan.budget;
    queryClient.setQueriesData<BoardView>({ queryKey: ["board"] }, (board) =>
      board?.budget ? { ...board, budget: { ...board.budget, ...reading } } : board,
    );
  }
}

/** Card-thread drawers show the same Gmail thread; refresh them when that thread changes. */
export function invalidateCardThreads(queryClient: QueryClient, threadKeys: readonly string[]) {
  if (!threadKeys.length) return;
  const keys = new Set(threadKeys);
  void queryClient.invalidateQueries({
    predicate: (q: Query) => q.queryKey[0] === "cardThread" && keys.has(String(q.queryKey[1])),
  });
}

/** After a reconnect we may have missed feed events: refresh what the feed screens show. */
export function invalidateFeedAfterReconnect(queryClient: QueryClient): void {
  for (const key of [
    "board",
    "snoozed",
    "card",
    "work",
    "proposals",
    "feeds",
    "notifications",
    "briefs",
    "tasks",
    "replies",
    "llmUsage",
    "senders",
    "sender",
    "unsubscribe",
    "compose",
    "composeDraft",
    "threadWork",
    "labels",
    "agenda",
  ]) {
    void queryClient.invalidateQueries({ queryKey: [key] });
  }
}
